import React from "react";
import {
  Eye,
  TrendingUp,
  Settings,
  FileText,
  Users,
  Download,
} from "lucide-react";
import {
  dummyDownloadTemplate,
  getAcroFormInfoByMemberId,
} from "../api/endpoints/dummyDownload";
import AssignToManager from "./AssignToManager";
import SelectMember from "./SelectMember";
import { SimpleModal } from "./SimpleModal";
import { SidePanel } from "./SidePanel";
import { XFAFormInfoPanel } from "./XFAFormInfoPanel";
import AcroFormInfoPanel from "./AcroFormInfoPanel";
import { AcroFormPreview } from "./AcroFormPreview";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchTemplates,
  TemplatesResponse,
  Template,
  submitForm,
} from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";
import XFAFormPreview from "./XFAFormPreview";
import TemplateAnalyticsPanel from "./TemplateAnalyticsPanel";
import { StatusBadge } from "./ui/Badge";
import TemplateListSkeleton from "./skeletons/TemplateListSkeleton";

interface TemplatesListProps {
  pendingUploadedFileId: string | null;
  onPendingFileIdConsumed: () => void;
  searchQuery?: string;
  statusFilter?: "all" | "published" | "draft";
}

export function TemplatesList({
  pendingUploadedFileId,
  onPendingFileIdConsumed,
  searchQuery,
  statusFilter = "all",
}: TemplatesListProps) {
  const { user, isAdmin, isManager } = useAuth();
  const queryClient = useQueryClient();

  // Single query for all roles - use different query keys but same function
  const templatesQuery = useQuery<TemplatesResponse>({
    queryKey: ["templates", isAdmin ? "admin" : "manager", user?.email ?? ""],
    queryFn: () => fetchTemplates(user?.email || ""),
    enabled: !!user?.email && (isAdmin || isManager),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const templatesLoading = templatesQuery.isLoading;
  const templatesFetching = templatesQuery.isFetching;
  const templatesError = templatesQuery.isError;
  const templatesErrorDetail = templatesQuery.error;

  const templates = React.useMemo<Template[]>(() => {
    const data = templatesQuery.data?.data ?? [];
    if (isAdmin) {
      return [...data];
    }
    // Manager: normalize defaults (pdfType/published) and use API response directly
    return data.map((t) => ({
      ...t,
      pdfType: !t.isAcroFile ? "XFA_PDF" : "ACROFORM_PDF",
    }));
  }, [isAdmin, templatesQuery.data?.data]);

  const filteredTemplates = React.useMemo(() => {
    const q = (searchQuery ?? "").trim().toLowerCase();
    let base = templates;
    if (q) {
      base = base.filter((t) => {
        const name = (t.fileTitle || t.fileName || "").toLowerCase();
        return name.includes(q);
      });
    }
    if (statusFilter === "published") {
      base = base.filter((t) => t.isPublished);
    } else if (statusFilter === "draft") {
      base = base.filter((t) => !t.isPublished);
    }

    return base;
  }, [templates, searchQuery, statusFilter]);

  const sortedTemplates = React.useMemo(() => {
    if (!filteredTemplates.length) return filteredTemplates;
    return [...filteredTemplates].sort((a, b) => {
      const aRef = a.updatedAt || a.createdAt;
      const bRef = b.updatedAt || b.createdAt;
      const aTime = aRef ? new Date(aRef).getTime() : 0;
      const bTime = bRef ? new Date(bRef).getTime() : 0;
      return bTime - aTime;
    });
  }, [filteredTemplates]);

  const [formPreviewPanelOpen, setFormPreviewPanelOpen] = React.useState(false);
  const [formInfoPanelOpen, setFormInfoPanelOpen] = React.useState(false);
  const [formAnalyticsPanelOpen, setFormAnalyticsPanelOpen] =
    React.useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<Template | null>(null);

  // Assignee panel state
  const [assigneePanelOpen, setAssigneePanelOpen] = React.useState(false);
  const [assigneePanelTemplate, setAssigneePanelTemplate] =
    React.useState<Template | null>(null);

  const highlightTimersRef = React.useRef<Map<string, number>>(new Map());
  // Local pending highlight when save happens from inside preview
  const [pendingInternalFileId, setPendingInternalFileId] = React.useState<
    string | null
  >(null);

  // Download handler
  const handleDownload = React.useCallback(
    async (memberId: string, fileId: string, isAcroFile: boolean) => {
      try {
        if (isAcroFile && user?.email) {
          // Download Acro Format PDF
          const response = await getAcroFormInfoByMemberId(
            user.email,
            memberId,
            fileId
          );
          const formData = JSON.parse(response.formData);
          const keyVlauePayload = toKeyValueMap(formData);
          submitForm(response.fileId, keyVlauePayload, true);
          console.log("Acro form download response:", response);
        } else {
          // Download dummy/XFA format
          await dummyDownloadTemplate({ memberId, fileId });
          console.log("Template download completed");
        }
      } catch (error) {
        console.error("Error downloading template:", error);
        // Optionally show a toast or error message to the user
      }
    },
    [user?.email]
  );

  function toKeyValueMap(
    formData: { key: string; value: string }[]
  ): Record<string, string[]> {
    return formData.reduce((acc, { key, value }) => {
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(value);
      return acc;
    }, {} as Record<string, string[]>);
  }

  React.useEffect(() => {
    const timers = highlightTimersRef.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  React.useEffect(() => {
    if (!pendingUploadedFileId) return;
    const matchingTemplate = sortedTemplates.find(
      (template) => template.fileId === pendingUploadedFileId
    );
    if (matchingTemplate) {
      onPendingFileIdConsumed();
    }
  }, [pendingUploadedFileId, sortedTemplates, onPendingFileIdConsumed]);

  const handleFormSaved = React.useCallback(
    (fileId: string) => {
      // Mirror AdminDashboard behavior: invalidate and schedule highlight once data is back
      setPendingInternalFileId(fileId);
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    [queryClient]
  );

  // When internal pending fileId is set, try to highlight once the refreshed list contains it
  React.useEffect(() => {
    if (!pendingInternalFileId) return;
    const match = sortedTemplates.find(
      (t) => t.fileId === pendingInternalFileId
    );
    if (match) {
      setPendingInternalFileId(null);
    }
  }, [pendingInternalFileId, sortedTemplates]);

  const Tooltip = ({
    label,
    children,
  }: {
    label: string;
    children: React.ReactNode;
  }) => {
    return (
      <div className="relative group inline-flex">
        {children}
        <div
          className="bg-primary text-on-primary pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2
                   whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white
                   opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {label}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative divide-y divide-gray-200">
        {templatesFetching && !templatesLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px] pointer-events-none">
            <span
              className="inline-block h-6 w-6 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin"
              role="status"
              aria-label="Refreshing templates"
            />
          </div>
        )}
        {templatesLoading && <TemplateListSkeleton />}
        {templatesError && (
          <div className="p-5 text-center text-red-500">
            Error loading templates. Please try again.
            {templatesErrorDetail instanceof Error && (
              <div className="mt-2 text-xs">{templatesErrorDetail.message}</div>
            )}
          </div>
        )}
        {!templatesLoading && !templatesError && templates.length === 0 && (
          <div className="p-5 text-center text-gray-500">
            No templates found.
          </div>
        )}
        {!templatesLoading && !templatesError && templates.length > 0 && (
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    ASSESSMENT NAME
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    VERSION
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortedTemplates.map((template) => {
                  const displayName = template.fileTitle || template.fileName;
                  const createdAt = template.createdAt
                    ? new Date(template.createdAt).toLocaleDateString()
                    : "—";

                  return (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-purple-600" />
                          <span className="text-sm font-medium text-gray-900">
                            {displayName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        v{template.version}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {createdAt}
                      </td>
                      <td className="px-6 py-4">
                        {isAdmin ? (
                          <StatusBadge
                            status={
                              template.isPublished ? "published" : "draft"
                            }
                          />
                        ) : (
                          <span className="text-sm text-gray-600">Pending</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Tooltip
                            label={isAdmin ? "Preview Form" : "View Form"}
                          >
                            <button
                              onClick={() => {
                                setSelectedTemplate(template);
                                setFormPreviewPanelOpen(true);
                              }}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>

                          {/* Assignee Icon */}
                          {template.isPublished && (
                            <Tooltip
                              label={
                                isAdmin
                                  ? "Assign to Manager"
                                  : isManager
                                  ? "Select Member"
                                  : "Assign"
                              }
                            >
                              <button
                                onClick={() => {
                                  setAssigneePanelTemplate(template);
                                  setAssigneePanelOpen(true);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title={
                                  isAdmin
                                    ? "Assign to Manager"
                                    : isManager
                                    ? "Select Member"
                                    : "Assign"
                                }
                              >
                                <Users className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}

                          {/* Assignee Modal */}
                          <SimpleModal
                            isOpen={assigneePanelOpen}
                            onClose={() => setAssigneePanelOpen(false)}
                            title={
                              isAdmin
                                ? "Assign to Manager"
                                : isManager
                                ? "Select Member"
                                : "Assign"
                            }
                          >
                            {assigneePanelTemplate &&
                              (isAdmin ? (
                                <AssignToManager
                                  htmlId={
                                    assigneePanelTemplate.htmlId ||
                                    assigneePanelTemplate.fileId
                                  }
                                  onAssigned={() => setAssigneePanelOpen(false)}
                                />
                              ) : isManager ? (
                                <SelectMember
                                  disabled={!!assigneePanelTemplate.memberId}
                                  htmlId={
                                    assigneePanelTemplate.htmlId ||
                                    assigneePanelTemplate.fileId
                                  }
                                  userId={user?.id || ""}
                                  onMemberAssigned={() => {
                                    setAssigneePanelOpen(false);
                                    void queryClient.invalidateQueries({
                                      queryKey: ["templates"],
                                    });
                                  }}
                                />
                              ) : null)}
                          </SimpleModal>

                          {isAdmin && (
                            <Tooltip label="View Analytics">
                              <button
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setFormAnalyticsPanelOpen(true);
                                }}
                                className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                              >
                                <TrendingUp className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip label="Form Settings">
                            <button
                              onClick={() => {
                                setSelectedTemplate(template);
                                setFormInfoPanelOpen(true);
                              }}
                              className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          </Tooltip>

                          {/* Download Icon */}
                          {!template.isPending && template.memberId != null && (
                            <Tooltip label="Download">
                              <button
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                title="Download Template"
                                onClick={() =>
                                  handleDownload(
                                    template.memberId,
                                    template.fileId,
                                    template.isAcroFile
                                  )
                                }
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formPreviewPanelOpen &&
        selectedTemplate &&
        (selectedTemplate.isAcroFile ? (
          <AcroFormPreview
            template={selectedTemplate}
            onClose={() => setFormPreviewPanelOpen(false)}
            onSaved={handleFormSaved}
            onInfo={() => {
              setFormInfoPanelOpen(true);
            }}
            isInfoPanelOpen={formInfoPanelOpen}
          />
        ) : (
          <XFAFormPreview
            template={selectedTemplate}
            onClose={() => setFormPreviewPanelOpen(false)}
            onSaved={handleFormSaved}
            onInfo={() => {
              setFormInfoPanelOpen(true);
            }}
            isInfoPanelOpen={formInfoPanelOpen}
          />
        ))}

      <SidePanel
        isOpen={formInfoPanelOpen}
        onClose={() => setFormInfoPanelOpen(false)}
        title="Form Info & Settings"
        size="md"
      >
        {selectedTemplate &&
          (selectedTemplate.pdfType === "ACROFORM_PDF" ? (
            <AcroFormInfoPanel template={selectedTemplate} />
          ) : (
            <XFAFormInfoPanel template={selectedTemplate} />
          ))}
      </SidePanel>

      {isAdmin && (
        <SidePanel
          isOpen={formAnalyticsPanelOpen}
          onClose={() => setFormAnalyticsPanelOpen(false)}
          title="Form Analytics"
          size="md"
        >
          {selectedTemplate && (
            <TemplateAnalyticsPanel template={selectedTemplate} />
          )}
        </SidePanel>
      )}
      {/* Assignment/member logic moved to Form Info & Settings panels */}
    </>
  );
}
